/**
 * Anomaly History Store
 * Tracks undo/redo operations for anomaly changes.
 */

import { create } from 'zustand';
import type { PhotoAnomaly } from '../types';

// Maximum number of history entries
const MAX_HISTORY = 50;

// Types of operations we can undo/redo
type OperationType = 'add' | 'update' | 'remove';

interface HistoryEntry {
  type: OperationType;
  anomalyId: string;
  before: PhotoAnomaly | null;  // State before the operation
  after: PhotoAnomaly | null;   // State after the operation
  timestamp: Date;
}

interface AnomalyHistoryStore {
  // History stacks
  past: HistoryEntry[];
  future: HistoryEntry[];
  
  // Actions
  recordAdd: (anomaly: PhotoAnomaly) => void;
  recordUpdate: (before: PhotoAnomaly, after: PhotoAnomaly) => void;
  recordRemove: (anomaly: PhotoAnomaly) => void;
  
  undo: (applyCallback: (entry: HistoryEntry) => void) => void;
  redo: (applyCallback: (entry: HistoryEntry) => void) => void;
  
  clearHistory: () => void;
  
  // Selectors
  canUndo: () => boolean;
  canRedo: () => boolean;
  getUndoDescription: () => string | null;
  getRedoDescription: () => string | null;
}

function getOperationDescription(entry: HistoryEntry): string {
  switch (entry.type) {
    case 'add':
      return `Add anomaly ${entry.after?.issueCode}`;
    case 'update':
      return `Update anomaly ${entry.after?.issueCode}`;
    case 'remove':
      return `Remove anomaly ${entry.before?.issueCode}`;
    default:
      return 'Unknown operation';
  }
}

export const useAnomalyHistoryStore = create<AnomalyHistoryStore>((set, get) => ({
  past: [],
  future: [],

  recordAdd: (anomaly: PhotoAnomaly) => {
    const entry: HistoryEntry = {
      type: 'add',
      anomalyId: anomaly.id,
      before: null,
      after: { ...anomaly },
      timestamp: new Date(),
    };
    
    set(state => ({
      past: [...state.past, entry].slice(-MAX_HISTORY),
      future: [], // Clear redo stack on new action
    }));
  },

  recordUpdate: (before: PhotoAnomaly, after: PhotoAnomaly) => {
    const entry: HistoryEntry = {
      type: 'update',
      anomalyId: after.id,
      before: { ...before },
      after: { ...after },
      timestamp: new Date(),
    };
    
    set(state => ({
      past: [...state.past, entry].slice(-MAX_HISTORY),
      future: [],
    }));
  },

  recordRemove: (anomaly: PhotoAnomaly) => {
    const entry: HistoryEntry = {
      type: 'remove',
      anomalyId: anomaly.id,
      before: { ...anomaly },
      after: null,
      timestamp: new Date(),
    };
    
    set(state => ({
      past: [...state.past, entry].slice(-MAX_HISTORY),
      future: [],
    }));
  },

  undo: (applyCallback: (entry: HistoryEntry) => void) => {
    const { past, future } = get();
    if (past.length === 0) return;
    
    const entry = past[past.length - 1];
    const newPast = past.slice(0, -1);
    
    // Apply the inverse operation
    applyCallback(entry);
    
    set({
      past: newPast,
      future: [entry, ...future].slice(0, MAX_HISTORY),
    });
  },

  redo: (applyCallback: (entry: HistoryEntry) => void) => {
    const { past, future } = get();
    if (future.length === 0) return;
    
    const entry = future[0];
    const newFuture = future.slice(1);
    
    // Reapply the operation
    applyCallback(entry);
    
    set({
      past: [...past, entry].slice(-MAX_HISTORY),
      future: newFuture,
    });
  },

  clearHistory: () => set({ past: [], future: [] }),

  canUndo: () => get().past.length > 0,
  
  canRedo: () => get().future.length > 0,

  getUndoDescription: () => {
    const { past } = get();
    if (past.length === 0) return null;
    return getOperationDescription(past[past.length - 1]);
  },

  getRedoDescription: () => {
    const { future } = get();
    if (future.length === 0) return null;
    return getOperationDescription(future[0]);
  },
}));
